import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TICKETS, META } from "../lib/content";
import { createRegistration, createCheckout } from "../lib/api";
import { useCatalog, ticketPrice, ticketCurrency, requiredNamedParticipants } from "../lib/useCatalog";
import { useTurnstile } from "../lib/useTurnstile";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useT } from "../lib/i18n";

// 2.6: klient generuje Idempotency-Key (UUID) dla checkout.
function newIdempotencyKey() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatPhone(raw) {
  let v = raw.replace(/[^\d+]/g, "");
  if (!v) return "";
  if (!v.startsWith("+")) v = "+" + v;

  if (v.startsWith("+48")) {
    const national = v.slice(3).replace(/\D/g, "").slice(0, 9);
    return ["+48", national.slice(0, 3), national.slice(3, 6), national.slice(6, 9)]
      .filter(Boolean)
      .join(" ");
  }

  const m = v.match(/^\+(\d{1,3})(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!m) return v.slice(0, 16);
  return ["+" + m[1], m[2], m[3], m[4]].filter(Boolean).join(" ");
}

export default function RegistrationModal({ open, onOpenChange, defaultTicket }) {
  const { t } = useT();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("+48 ");
  const [telegram, setTelegram] = useState("");
  const [ticket, setTicket] = useState(defaultTicket || "standard");
  // 2.5: zgoda domyslnie WYLACZONA — musi byc jawnie zaznaczona.
  const [agree, setAgree] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultTicket) setTicket(defaultTicket);
  }, [defaultTicket, open]);

  const { catalog } = useCatalog();
  const selectedTicket = TICKETS.find((tk) => tk.key === ticket);
  const ticketTrans = t(`pricing.tickets.${ticket}`) || {};
  const selectedPrice = ticketPrice(catalog, ticket);
  const selectedCurrency = ticketCurrency(catalog, ticket);
  const requiredNamed = requiredNamedParticipants(catalog, ticket);

  // 2.6: payment-registration Turnstile — OSOBNY site key/action od contact.
  const paymentSiteKey = process.env.REACT_APP_PAYMENT_TURNSTILE_SITE_KEY;
  const { containerRef: turnstileRef, token: paymentToken, reset: resetTurnstile } = useTurnstile({
    siteKey: paymentSiteKey,
    action: META?.paymentTurnstileAction || "wrocon_registration",
    enabled: open,
  });

  // 2.5: dopasuj liczbe pol named participants do wybranego biletu.
  useEffect(() => {
    setParticipants((prev) => {
      const next = prev.slice(0, requiredNamed);
      while (next.length < requiredNamed) next.push("");
      return next;
    });
  }, [requiredNamed, ticket]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTicket?.soldOut) {
      const message = t("toasts.closed");
      toast.error(message === "toasts.closed" ? "Ticket sales are closed." : message);
      return;
    }
    if (!fullName.trim()) return toast.error(t("modal.err_name"));
    if (phone.replace(/\D/g, "").length < 8) return toast.error(t("modal.err_phone"));
    if (!telegram.trim()) return toast.error(t("modal.err_tg"));
    if (!agree) return toast.error(t("modal.err_agree"));
    // 2.5: named participants wymagane dla biletow named_participants.
    const namedParticipants = participants.map((p) => p.trim()).filter(Boolean);
    if (namedParticipants.length !== requiredNamed) {
      return toast.error(t("modal.err_participants"));
    }
    // 2.6: payment Turnstile wymagany do rejestracji (fail-closed w UI gdy skonfigurowany).
    if (paymentSiteKey && !paymentToken) {
      return toast.error(t("modal.err_turnstile"));
    }

    setLoading(true);
    try {
      // 2.6: payment Turnstile token idzie WYLACZNIE do rejestracji, raz.
      const registration = await createRegistration({
        full_name: fullName.trim(),
        phone: phone.trim(),
        telegram: telegram.trim().replace(/^@/, ""),
        ticket_type: ticket,
        agree,
        participants: namedParticipants.map((name) => ({ full_name: name })),
        payment_turnstile_token: paymentToken || undefined,
      });
      // token zuzyty — reset widgetu, by nie wyslac go ponownie.
      resetTurnstile();
      // 2.6: nonce z rejestracji + client Idempotency-Key naglowkiem; BEZ payment tokenu w checkout.
      const checkout = await createCheckout(
        {
          registration_id: registration.id,
          origin_url: window.location.origin,
          nonce: registration.checkout_nonce,
        },
        { idempotencyKey: newIdempotencyKey() },
      );
      if (!checkout?.url || !checkout?.session_id) {
        throw new Error(t("modal.err_generic"));
      }
      try {
        localStorage.setItem("last_session_id", checkout.session_id);
      } catch (_) {}
      window.location.href = checkout.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(String(detail || err?.message || t("modal.err_generic")));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="registration-modal" className="max-w-xl bg-[#F8F5F2] border border-[#E2D8CF] rounded-none p-0 overflow-y-auto max-h-[90vh]">
        <div className="bg-[#6E1A24] text-[#F8F5F2] px-6 py-5">
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display uppercase tracking-wide text-2xl">{t("modal.title")}</DialogTitle>
            <DialogDescription className="text-[#F8F5F2]/80 text-sm">{t("modal.desc")}</DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} noValidate className="px-6 py-6 space-y-6" data-testid="registration-form">
          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_name")}</label>
            <input data-testid="input-fullname" className="input-underline" placeholder={t("modal.p_name")} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_phone")}</label>
            <input data-testid="input-phone" className="input-underline" placeholder={t("modal.p_phone")} value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} inputMode="tel" />
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-1">{t("modal.l_tg")}</label>
            <input data-testid="input-telegram" className="input-underline" placeholder={t("modal.p_tg")} value={telegram} onChange={(e) => setTelegram(e.target.value)} />
            <p className="text-xs text-ink2 mt-2">{t("modal.help_tg")}</p>
          </div>

          <div>
            <label className="eyebrow text-ink2 block mb-2">{t("modal.l_ticket")}</label>
            <Select value={ticket} onValueChange={setTicket}>
              <SelectTrigger data-testid="select-ticket" className="rounded-none bg-transparent border-0 border-b border-[#1A1A1A] focus:border-[#6E1A24] focus:ring-0 px-0 h-12 text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[#F8F5F2] border-[#E2D8CF]">
                {TICKETS.filter((tk) => !tk.hidden).map((tk) => {
                  const name = t(`pricing.tickets.${tk.key}.name`) || tk.key;
                  const p = ticketPrice(catalog, tk.key);
                  const c = ticketCurrency(catalog, tk.key);
                  return (
                    <SelectItem key={tk.key} value={tk.key} data-testid={`select-ticket-option-${tk.key}`} className="rounded-none">
                      {name}{p === null ? "" : ` — ${p} ${c}`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {ticketTrans.name && (
              <p className="text-xs text-ink2 mt-2">{ticketTrans.name}{ticketTrans.sub ? ` · ${ticketTrans.sub}` : ""}</p>
            )}
          </div>

          {requiredNamed > 0 && (
            <div data-testid="participants-section" className="space-y-3">
              <label className="eyebrow text-ink2 block">{t("modal.l_participants")}</label>
              {participants.map((value, i) => (
                <input
                  key={i}
                  data-testid={`input-participant-${i}`}
                  className="input-underline"
                  placeholder={t("modal.p_participant")}
                  value={value}
                  onChange={(e) => {
                    const next = participants.slice();
                    next[i] = e.target.value;
                    setParticipants(next);
                  }}
                />
              ))}
              <p className="text-xs text-ink2">{t("modal.help_participants")}</p>
            </div>
          )}

          <div className="bg-[#F2EBE5] border border-[#E2D8CF] p-4 flex items-center justify-between">
            <div>
              <div className="eyebrow text-ink2">{t("modal.total")}</div>
              <div data-testid="modal-total-price" className="font-display text-3xl">{selectedPrice === null ? "—" : `${selectedPrice} ${selectedCurrency}`}</div>
            </div>
            <div className="text-right text-xs text-ink2 max-w-[60%]">{t("modal.pay_note")}</div>
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-3 text-xs text-ink2 cursor-pointer">
              <input data-testid="checkbox-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#6E1A24]" />
              <span>{t("modal.agree")}</span>
            </label>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink2 pl-7">
              <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="legal-terms" className="underline hover:text-[#6E1A24]">{t("footer.terms")}</a>
              <a href="/refund" target="_blank" rel="noopener noreferrer" data-testid="legal-refund" className="underline hover:text-[#6E1A24]">{t("footer.refund")}</a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="legal-privacy" className="underline hover:text-[#6E1A24]">{t("footer.privacy")}</a>
            </div>
          </div>

          {paymentSiteKey && (
            <div data-testid="payment-turnstile" ref={turnstileRef} className="flex justify-center" />
          )}

          <button type="submit" disabled={loading} data-testid="submit-payment-btn" className="btn-primary w-full px-8 py-4 inline-flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t("modal.pay_loading")}</>
            ) : (
              <><Lock className="w-4 h-4" /> {t("modal.pay_btn")}</>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
